import { RangeHorizon } from '@toggle/chart';
import { DropdownMenu } from '@toggle/design-system';
import { InsightCondition } from '@toggle/toggle';
import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Option } from '~/components/filter/Option';
import { WATCHLIST_ID, WORKING_LIST_ID } from '~/config/constants';
import { useInsightsFullDrawer } from '~/hooks/use-insights-full-drawer/useInsightsFullDrawer';
import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { useExplore } from '~/react-query-hooks/explore/useExplore';
import { useCompanyCompetitorWithThemes } from '~/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { queryKeys } from '~/routes/query-params';
import { Tracking } from '~/services/tracking';
import { PosthogTrackingSource } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useWatchlist } from '~/stores/use-watchlist/useWatchlist';
import { SelectedAssetItem } from '~/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/run-scenario/RunScenario';
import { RangeHorizonValue } from '~/views/insights/components/insights-full-drawer/insights-left-section/InsightsLeftSection';

import { useScenarioFromInsights } from '../../hooks/useScenarioFromInsights';
import * as S from './ScenarioTriggerSection.styles';

interface ScenarioTriggerSectionProps {
  entityName: string;
  entityTag: string;
  insightId: string;
  showViewMore?: boolean;
  conditions: InsightCondition[];
}

export const ScenarioTriggerSection = ({
  entityName,
  entityTag,
  insightId,
  showViewMore,
  conditions,
}: ScenarioTriggerSectionProps) => {
  const { t } = useTranslation(['insights']);

  const { openDrawer } = useInsightsFullDrawer();
  const { trackInsightsDrawerOpen } = usePosthogEvents();
  const { params } = useQueryParams();
  const lists = useWatchlist(state => state.lists);
  const [selectedAssetsItem, setSelectedAssetsItem] =
    useState<SelectedAssetItem>();

  const { onRunScenario } = useScenarioFromInsights({
    entityTag,
    conditions,
  });

  const { data } = useCompanyCompetitorWithThemes({
    entityTag,
  });

  const { data: insightsData, isLoading: isInsightsLoading } = useExplore({
    entityTags: showViewMore ? [entityTag] : [],
    filters: {
      entity_tag: {
        values: [
          {
            tags: [entityTag],
          },
        ],
      },
      period: { values: [RangeHorizonValue[RangeHorizon.OneMonth]] },
    },
    sortBy: undefined,
    combined: true,
  });

  if (isInsightsLoading) {
    return <S.StyledLoader />;
  }

  const onOptionClick = (selectedAssetItem: SelectedAssetItem) => {
    setSelectedAssetsItem(selectedAssetItem);
  };

  const availableInsights =
    insightsData?.pages?.[0]?.entities[0]?.insights ?? [];
  const additionalInsightsCount = availableInsights.length;

  const pillOptionsWithHeader = {
    ...(data
      ? {
          Connections: [
            {
              key: WORKING_LIST_ID,
              name: t('insights:runScenarioSection.peers'),
              value: { value: WORKING_LIST_ID },
            },
          ],
        }
      : {}),
    ...(lists.length > 0
      ? {
          Watchlist: lists.map(list => ({
            key: WATCHLIST_ID,
            name: list.name,
            value: { value: list.id },
          })),
        }
      : {}),
  };

  const onClickViewAll = () => {
    const trackingProperties = params[queryKeys.source];
    let parsedTrackingProperties: Record<string, string> = {};

    try {
      if (trackingProperties) {
        const parsed = JSON.parse(trackingProperties);

        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          parsedTrackingProperties = parsed;
        }

        const { source, ...rest } = parsedTrackingProperties;
        trackInsightsDrawerOpen(source as PosthogTrackingSource, 'full', rest);
      }
    } catch (error) {
      Tracking.captureException(error);
    }

    openDrawer({ entityTag, insightId });
  };

  const hasOptions = Object.keys(pillOptionsWithHeader).length > 0;

  if (!hasOptions) {
    return null;
  }

  const firstOption = Object.values(pillOptionsWithHeader)[0][0];
  const selectedWatchlistName = lists.find(
    list => list.id === selectedAssetsItem?.id
  )?.name;
  return (
    <S.Root>
      {additionalInsightsCount > 1 && (
        <S.Row>
          <S.FlexColumn>
            <S.TitleLabel>
              {t('insights:viewAllSection.whatElseIsMoving', {
                name: entityName,
              })}
            </S.TitleLabel>
            <S.DescriptionLabel>
              {t('insights:viewAllSection.moreInsights', {
                count: additionalInsightsCount - 1,
              })}
            </S.DescriptionLabel>
          </S.FlexColumn>
          <S.StyledButton
            variant="primary"
            label={t('insights:viewAllSection.viewAll')}
            onClick={onClickViewAll}
          />
        </S.Row>
      )}
      <S.Row>
        <S.FlexColumn>
          <S.TitleLabel>
            {t('insights:runScenarioSection.title', {
              name: entityName,
            })}
          </S.TitleLabel>
          <div>
            <DropdownMenu
              triggerNode={
                <S.StyledChip
                  label={selectedWatchlistName || firstOption.name}
                  rightIcon="ChevronLightDown"
                  rightIconSize={12}
                  type="input"
                />
              }
            >
              <S.ScrollableList>
                {Object.entries(pillOptionsWithHeader).map(
                  ([header, options], index) => {
                    const isLast =
                      index === Object.keys(pillOptionsWithHeader).length - 1;
                    return (
                      <Fragment key={header}>
                        <div>
                          <S.CustomHeaderLabel>{header}</S.CustomHeaderLabel>
                          {options.map(option => (
                            <Option
                              key={option.name}
                              option={option}
                              handleClick={() =>
                                onOptionClick({
                                  assetFilterValueKey:
                                    option.key === WORKING_LIST_ID
                                      ? WORKING_LIST_ID
                                      : WATCHLIST_ID,
                                  id: option.value.value,
                                })
                              }
                            />
                          ))}
                        </div>
                        {!isLast && <S.Separator />}
                      </Fragment>
                    );
                  }
                )}
              </S.ScrollableList>
            </DropdownMenu>
          </div>
        </S.FlexColumn>
        <S.StyledButton
          variant="primary"
          label={t('insights:runScenarioSection.ctaButton')}
          onClick={() =>
            onRunScenario(
              selectedAssetsItem ?? {
                assetFilterValueKey: firstOption.key,
                id: firstOption.value.value,
              }
            )
          }
        />
      </S.Row>
    </S.Root>
  );
};
